import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';

interface MetaTemplate {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: {
    type: string;
    text?: string;
    example?: {
      body_text?: string[][];
    };
  }[];
}

interface DraftTemplate {
  id: string;
  name: string;
  content: string;
  header?: string;
  footer?: string;
  variables: string[];
  category: string;
  language: string;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  metaTemplateId?: string;
  buttons?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const TemplatesView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'approved' | 'drafts'>('approved');
  
  // Meta templates state
  const [metaTemplates, setMetaTemplates] = useState<MetaTemplate[]>([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  
  // Draft templates state
  const [draftTemplates, setDraftTemplates] = useState<DraftTemplate[]>([]);
  const [draftLoading, setDraftLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DraftTemplate | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (activeTab === 'approved') {
      loadMetaTemplates();
    } else {
      loadDraftTemplates();
    }
  }, [activeTab, statusFilter]);

  const loadMetaTemplates = async () => {
    try {
      setMetaLoading(true);
      const response = await axios.get('/templates');
      setMetaTemplates(response.data);
    } catch (error) {
      console.error('Error loading Meta templates:', error);
      alert('Failed to load templates. Please check your WhatsApp Business Account ID configuration.');
    } finally {
      setMetaLoading(false);
    }
  };

  const loadDraftTemplates = async () => {
    try {
      setDraftLoading(true);
      const url = statusFilter === 'ALL' 
        ? '/templates/drafts' 
        : `/templates/drafts?status=${statusFilter}`;
      const response = await axios.get(url);
      if (response.data.success) {
        setDraftTemplates(response.data.data);
      }
    } catch (error) {
      console.error('Error loading draft templates:', error);
      alert('Failed to load draft templates');
    } finally {
      setDraftLoading(false);
    }
  };

  const categories = ['ALL', ...Array.from(new Set(metaTemplates.map((t: MetaTemplate) => t.category)))];

  const filteredMetaTemplates = metaTemplates.filter((template: MetaTemplate) => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'ALL' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getMetaTemplateBody = (template: MetaTemplate): string => {
    const bodyComponent = template.components.find(c => c.type === 'BODY');
    return bodyComponent?.text || 'No preview available';
  };

  const getMetaTemplateHeader = (template: MetaTemplate): string | null => {
    const headerComponent = template.components.find(c => c.type === 'HEADER');
    return headerComponent?.text || null;
  };

  const getMetaTemplateFooter = (template: MetaTemplate): string | null => {
    const footerComponent = template.components.find(c => c.type === 'FOOTER');
    return footerComponent?.text || null;
  };

  const getMetaVariableCount = (template: MetaTemplate): number => {
    const body = getMetaTemplateBody(template);
    const matches = body.match(/\{\{\d+\}\}/g);
    return matches ? matches.length : 0;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return '#6b7280';
      case 'PENDING': return '#f59e0b';
      case 'APPROVED': return '#10b981';
      case 'REJECTED': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Draft';
      case 'PENDING': return 'Pending Approval';
      case 'APPROVED': return 'Approved';
      case 'REJECTED': return 'Rejected';
      default: return status;
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`/templates/drafts/${id}`);
      loadDraftTemplates();
      alert('Template deleted successfully');
    } catch (error: any) {
      console.error('Error deleting template:', error);
      alert(error.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleSubmit = async (id: string) => {
    if (!window.confirm('Mark this template as pending? You can then submit it to Meta WhatsApp Manager for approval.')) return;
    
    try {
      await axios.post(`/templates/drafts/${id}/submit`);
      loadDraftTemplates();
      alert('Template marked as pending. Please submit it to Meta WhatsApp Manager for approval.');
    } catch (error: any) {
      console.error('Error submitting template:', error);
      alert(error.response?.data?.error || 'Failed to submit template');
    }
  };

  return (
    <div className="contacts-view" style={{ padding: '24px' }}>
      <div className="contacts-header">
        <div>
          <h2 className="contacts-title">Message Templates</h2>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {activeTab === 'approved' 
              ? (metaLoading ? 'Loading...' : `${filteredMetaTemplates.length} of ${metaTemplates.length} approved template${metaTemplates.length !== 1 ? 's' : ''}`)
              : (draftLoading ? 'Loading...' : `${draftTemplates.length} draft template${draftTemplates.length !== 1 ? 's' : ''}`)
            }
          </p>
        </div>
        {activeTab === 'drafts' && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="add-contact-button"
              onClick={() => setShowCreateModal(true)}
            >
              + Create Template
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '24px', display: 'flex', gap: '8px', borderBottom: '2px solid #e5e7eb' }}>
        <button
          onClick={() => setActiveTab('approved')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeTab === 'approved' ? '2px solid #1e40af' : '2px solid transparent',
            color: activeTab === 'approved' ? '#1e40af' : '#6b7280',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'approved' ? '600' : '400',
            marginBottom: '-2px'
          }}
        >
          Approved Templates
        </button>
        <button
          onClick={() => setActiveTab('drafts')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            borderBottom: activeTab === 'drafts' ? '2px solid #1e40af' : '2px solid transparent',
            color: activeTab === 'drafts' ? '#1e40af' : '#6b7280',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: activeTab === 'drafts' ? '600' : '400',
            marginBottom: '-2px'
          }}
        >
          Draft Templates
        </button>
      </div>

      {activeTab === 'approved' ? (
        <>
          {/* Search and Category Filter for Approved Templates */}
          <div style={{ marginBottom: '24px' }}>
            <div className="search-container" style={{ marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="🔍 Search templates..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    background: selectedCategory === category ? '#1e40af' : 'white',
                    color: selectedCategory === category ? 'white' : '#1f2937',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: selectedCategory === category ? '600' : '400'
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Info Banner */}
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#1e40af'
          }}>
            <strong>📝 Note:</strong> These are approved templates from{' '}
            <a 
              href="https://business.facebook.com/wa/manage/message-templates/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1e40af', textDecoration: 'underline' }}
            >
              Meta WhatsApp Manager
            </a>
            . To create or modify templates, use the Draft Templates tab.
          </div>

          {metaLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
              Loading templates...
            </div>
          ) : filteredMetaTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }}>📋</div>
              <h4>No templates found</h4>
              <p style={{ marginTop: '8px' }}>
                {searchTerm || selectedCategory !== 'ALL'
                  ? 'Try adjusting your search or filter'
                  : 'No approved templates found. Create templates in Meta WhatsApp Manager.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
              {filteredMetaTemplates.map((template) => {
                const header = getMetaTemplateHeader(template);
                const body = getMetaTemplateBody(template);
                const footer = getMetaTemplateFooter(template);
                const variableCount = getMetaVariableCount(template);
                
                return (
                  <div 
                    key={template.id}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{ marginBottom: '12px' }}>
                      <h3 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        marginBottom: '8px',
                        color: '#111827'
                      }}>
                        {template.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </h3>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          fontWeight: '600'
                        }}>
                          {template.category}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {template.language.toUpperCase()}
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          backgroundColor: '#dcfce7',
                          color: '#166534',
                          fontWeight: '600'
                        }}>
                          {template.status}
                        </span>
                        {variableCount > 0 && (
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#fae8ff',
                            color: '#86198f',
                            fontWeight: '500'
                          }}>
                            {variableCount} variable{variableCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    {header && (
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#6b7280', 
                        marginBottom: '8px',
                        fontStyle: 'italic',
                        fontWeight: '500'
                      }}>
                        <strong>Header:</strong> {header}
                      </div>
                    )}

                    <div style={{ 
                      fontSize: '14px', 
                      color: '#1f2937', 
                      marginBottom: '8px',
                      lineHeight: '1.5',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {body}
                    </div>

                    {footer && (
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#6b7280', 
                        marginBottom: '8px',
                        fontStyle: 'italic',
                        fontWeight: '500'
                      }}>
                        <strong>Footer:</strong> {footer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Status Filter for Draft Templates */}
          <div style={{ marginBottom: '24px', display: 'flex', gap: '8px' }}>
            {['ALL', 'DRAFT', 'PENDING', 'APPROVED', 'REJECTED'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: statusFilter === status ? '#1e40af' : 'white',
                  color: statusFilter === status ? 'white' : '#1f2937',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: statusFilter === status ? '600' : '400'
                }}
              >
                {status === 'ALL' ? 'All' : getStatusLabel(status)}
              </button>
            ))}
          </div>

          {/* Info Banner */}
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#1e40af'
          }}>
            <strong>📝 Note:</strong> Templates must be created and approved in{' '}
            <a 
              href="https://business.facebook.com/wa/manage/message-templates/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1e40af', textDecoration: 'underline' }}
            >
              Meta WhatsApp Manager
            </a>
            {' '}before they can be used. Use this tool to draft and prepare templates before submission.
          </div>

          {draftLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
              Loading templates...
            </div>
          ) : draftTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.3 }}>📋</div>
              <h4>No templates found</h4>
              <p style={{ marginTop: '8px' }}>
                {statusFilter !== 'ALL' 
                  ? `No ${getStatusLabel(statusFilter).toLowerCase()} templates` 
                  : 'Create your first template to get started'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '20px' }}>
              {draftTemplates.map((template) => (
                <div 
                  key={template.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                        {template.name}
                      </h3>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '11px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          backgroundColor: getStatusColor(template.status) + '20',
                          color: getStatusColor(template.status),
                          fontWeight: '600'
                        }}>
                          {getStatusLabel(template.status)}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {template.category}
                        </span>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {template.language.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowEditModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(template.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          border: '1px solid #fee2e2',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#dc2626',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {template.header && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#6b7280', 
                      marginBottom: '8px',
                      fontStyle: 'italic'
                    }}>
                      <strong>Header:</strong> {template.header}
                    </div>
                  )}

                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1f2937', 
                    marginBottom: '8px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {template.content}
                  </div>

                  {template.footer && (
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#6b7280', 
                      marginBottom: '8px',
                      fontStyle: 'italic'
                    }}>
                      <strong>Footer:</strong> {template.footer}
                    </div>
                  )}

                  {template.variables.length > 0 && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280', 
                      marginBottom: '8px'
                    }}>
                      <strong>Variables:</strong> {template.variables.join(', ')}
                    </div>
                  )}

                  {template.rejectionReason && (
                    <div style={{
                      backgroundColor: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      padding: '12px',
                      marginTop: '12px',
                      fontSize: '13px',
                      color: '#991b1b'
                    }}>
                      <strong>Rejection Reason:</strong> {template.rejectionReason}
                    </div>
                  )}

                  {template.notes && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280', 
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #f3f4f6'
                    }}>
                      <strong>Notes:</strong> {template.notes}
                    </div>
                  )}

                  {template.status === 'DRAFT' && (
                    <button
                      onClick={() => handleSubmit(template.id)}
                      style={{
                        marginTop: '12px',
                        width: '100%',
                        padding: '8px',
                        fontSize: '13px',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        background: '#3b82f6',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Mark as Pending
                    </button>
                  )}

                  {template.status === 'PENDING' && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e'
                    }}>
                      <strong>Next Step:</strong> Submit this template to{' '}
                      <a 
                        href="https://business.facebook.com/wa/manage/message-templates/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#92400e', textDecoration: 'underline' }}
                      >
                        Meta WhatsApp Manager
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <TemplateFormModal
          template={selectedTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedTemplate(null);
          }}
          onSave={() => {
            loadDraftTemplates();
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}
    </div>
  );
};

// Template Form Modal Component
interface TemplateFormModalProps {
  template?: DraftTemplate | null;
  onClose: () => void;
  onSave: () => void;
}

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({ template, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    content: template?.content || '',
    header: template?.header || '',
    footer: template?.footer || '',
    category: template?.category || 'UTILITY',
    language: template?.language || 'en',
    notes: template?.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    } else if (!/^[a-z0-9_]+$/.test(formData.name)) {
      newErrors.name = 'Name must be lowercase, alphanumeric, and can only contain underscores';
    } else if (formData.name.length > 512) {
      newErrors.name = 'Name must be 512 characters or less';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > 1024) {
      newErrors.content = 'Content must be 1024 characters or less';
    }

    if (formData.header && formData.header.length > 60) {
      newErrors.header = 'Header must be 60 characters or less';
    }

    if (formData.footer && formData.footer.length > 60) {
      newErrors.footer = 'Footer must be 60 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      if (template) {
        // Update existing template
        await axios.put(`/templates/drafts/${template.id}`, formData);
        alert('Template updated successfully!');
      } else {
        // Create new template
        await axios.post('/templates/drafts', formData);
        alert('Template created successfully!');
      }
      onSave();
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Extract variables from content
  const variableMatches = formData.content.match(/\{\{(\d+)\}\}/g) || [];
  const variables = Array.from(new Set(variableMatches.map(m => m.match(/\d+/)?.[0] || ''))).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <div className="assignment-modal-overlay" onClick={onClose}>
      <div className="assignment-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="assignment-header">
          <h4>{template ? 'Edit Template' : 'Create New Template'}</h4>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="assignment-content">
            <div style={{ 
              backgroundColor: '#eff6ff', 
              padding: '12px', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#1e40af'
            }}>
              <strong>💡 Tips:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Use <code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>, etc. for variables</li>
                <li>Header: max 60 characters</li>
                <li>Body: max 1024 characters</li>
                <li>Footer: max 60 characters</li>
                <li>Name must be lowercase with underscores only</li>
              </ul>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Template Name * (lowercase, alphanumeric, underscores only)
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase() })}
                className="form-input"
                placeholder="welcome_message"
                required
                disabled={!!template}
                style={{ fontFamily: 'monospace' }}
              />
              {errors.name && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.name}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.name.length}/512 characters
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="form-input"
                  required
                >
                  <option value="UTILITY">Utility</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                  Language *
                </label>
                  <input
                    type="text"
                    value={formData.language}
                    onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    className="form-input"
                    placeholder="en"
                    required
                  />
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Header (Optional, max 60 chars)
              </label>
              <input
                type="text"
                value={formData.header}
                onChange={(e) => setFormData({ ...formData, header: e.target.value })}
                className="form-input"
                placeholder="Welcome!"
                maxLength={60}
              />
              {errors.header && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.header}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.header.length}/60 characters
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Body Content * (max 1024 chars)
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="form-input"
                placeholder="Hello {{1}}, welcome to our service!"
                rows={6}
                required
                maxLength={1024}
              />
              {errors.content && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.content}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.content.length}/1024 characters
                {variables.length > 0 && ` • Variables: ${variables.join(', ')}`}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Footer (Optional, max 60 chars)
              </label>
              <input
                type="text"
                value={formData.footer}
                onChange={(e) => setFormData({ ...formData, footer: e.target.value })}
                className="form-input"
                placeholder="Powered by WhatsApp"
                maxLength={60}
              />
              {errors.footer && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.footer}</div>}
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                {formData.footer.length}/60 characters
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px' }}>
                Internal Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input"
                placeholder="Add any notes about this template..."
                rows={3}
              />
            </div>
          </div>

          <div className="assignment-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="assign-button" disabled={saving}>
              {saving ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplatesView;

